import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  getPatientLab,
  getPatientLabs,
  uploadPatientLabReport as _uploadPatientLabReport,
  postPatientLabFeedback as _postPatientLabFeedback,
} from "../../../fay/Api";
import {
  UploadPatientLabPayload,
  PatientLabResponse,
} from "../../../fay/generated";
import { AxiosError } from "axios";

const useLabs = () => {
  const queryClient = useQueryClient();

  const {
    mutate: createLabSummary,
    isPending: isCreatingLabSummary,
    isError: isCreatingLabSummaryError,
    error: _createLabSummaryError,
  } = useMutation({
    mutationFn: async (payload: UploadPatientLabPayload) => {
      return await _uploadPatientLabReport("me", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labs"] });
    },
  });

  let createLabSummaryError = null;
  if (_createLabSummaryError !== null) {
    if (_createLabSummaryError instanceof AxiosError) {
      if (_createLabSummaryError.response?.status === 422) {
        const detail = _createLabSummaryError.response?.data.detail[0];

        createLabSummaryError = detail.msg.split(", ")[1];
      }
    }

    if (createLabSummaryError === null) {
      createLabSummaryError = _createLabSummaryError.message;
    }
  }

  const useGetLabs = (patientId: string | undefined) =>
    useQuery({
      queryKey: ["labs"],
      queryFn: async () => {
        if (!patientId) {
          throw new Error("No patient ID provided");
        }

        return (await getPatientLabs(patientId)).patient_labs;
      },
    });

  const useGetLab = (patientId: string, labId: string) =>
    useQuery({
      queryKey: ["lab", patientId, labId],
      queryFn: async () => {
        return await getPatientLab(patientId, labId);
      },
      // Only refetch if the link has expired
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: 1000 * 60 * 60, // 1 hour
    });

  const { mutateAsync: postPatientLabFeedback } = useMutation({
    mutationFn: async (payload: {
      patientId: string;
      labId: string;
      isFeedbackPositive: boolean;
    }) => {
      return _postPatientLabFeedback(payload);
    },
  });

  const addLabToCache = useCallback(
    (lab: PatientLabResponse, patientId: string) => {
      queryClient.setQueryData(["lab", patientId, lab.patient_lab_id], lab);
    },
    [queryClient],
  );

  return {
    useGetLab,
    useGetLabs,
    createLabSummary,
    isCreatingLabSummary,
    isCreatingLabSummaryError,
    createLabSummaryError,
    postPatientLabFeedback,
    addLabToCache,
  };
};

export default useLabs;
